import { useState } from 'react';

interface DataPoint {
  label: string;
  value: number;
}

const monthlyData: DataPoint[] = [
  { label: 'נוב׳', value: 18 },
  { label: 'דצמ׳', value: 24 },
  { label: 'ינו׳', value: 31 },
  { label: 'פבר׳', value: 27 },
  { label: 'מרץ', value: 42 },
  { label: 'אפר׳', value: 38 },
];

const weeklyData: DataPoint[] = [
  { label: 'א׳', value: 4 },
  { label: 'ב׳', value: 7 },
  { label: 'ג׳', value: 5 },
  { label: 'ד׳', value: 9 },
  { label: 'ה׳', value: 6 },
  { label: 'ו׳', value: 3 },
  { label: 'ש׳', value: 2 },
];

function buildPath(points: { x: number; y: number }[]): string {
  if (points.length < 2) return '';
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const cpx = (prev.x + curr.x) / 2;
    d += ` C ${cpx} ${prev.y}, ${cpx} ${curr.y}, ${curr.x} ${curr.y}`;
  }
  return d;
}

function buildAreaPath(points: { x: number; y: number }[], height: number): string {
  if (points.length < 2) return '';
  const linePath = buildPath(points);
  return `${linePath} L ${points[points.length - 1].x} ${height} L ${points[0].x} ${height} Z`;
}

export default function RegistrationsChart() {
  const [view, setView] = useState<'monthly' | 'weekly'>('monthly');
  const [hovered, setHovered] = useState<number | null>(null);

  const data = view === 'monthly' ? monthlyData : weeklyData;

  const W = 560;
  const H = 160;
  const padX = 20;
  const padY = 16;

  const maxVal = Math.max(...data.map((d) => d.value));
  const minVal = 0;

  const points = data.map((d, i) => ({
    x: padX + (i / (data.length - 1)) * (W - padX * 2),
    y: padY + (1 - (d.value - minVal) / (maxVal - minVal || 1)) * (H - padY * 2),
  }));

  const linePath = buildPath(points);
  const areaPath = buildAreaPath(points, H);

  const total = data.reduce((s, d) => s + d.value, 0);
  const peak = data.reduce((a, b) => (a.value > b.value ? a : b));

  return (
    <div className="bg-white rounded-2xl border-2 border-rose-100 p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-bold text-gray-800">נרשמים לאורך זמן</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {view === 'monthly' ? 'חצי שנה אחרונה' : 'שבוע נוכחי'}
          </p>
        </div>
        <div className="flex items-center gap-1 bg-rose-50 border-2 border-rose-100 rounded-xl p-1">
          {(['monthly', 'weekly'] as const).map((v) => (
            <button
              key={v}
              onClick={() => { setView(v); setHovered(null); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                view === v ? 'bg-rose-400 text-white' : 'text-gray-500 hover:bg-rose-100'
              }`}
            >
              {v === 'monthly' ? 'חודשי' : 'שבועי'}
            </button>
          ))}
        </div>
      </div>

      {/* Summary pills */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 border border-rose-100 rounded-xl">
          <div className="w-2 h-2 rounded-full bg-rose-400"></div>
          <span className="text-xs font-semibold text-gray-600">סה״כ: <strong className="text-gray-800">{total}</strong></span>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-100 rounded-xl">
          <div className="w-2 h-2 rounded-full bg-amber-400"></div>
          <span className="text-xs font-semibold text-gray-600">שיא: <strong className="text-gray-800">{peak.value} ({peak.label})</strong></span>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-100 rounded-xl">
          <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
          <span className="text-xs font-semibold text-gray-600">ממוצע: <strong className="text-gray-800">{Math.round(total / data.length)}</strong></span>
        </div>
      </div>

      {/* SVG Chart */}
      <div className="relative w-full overflow-hidden" dir="ltr">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full"
          style={{ height: 160 }}
          onMouseLeave={() => setHovered(null)}
        >
          <defs>
            <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fb7185" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#fb7185" stopOpacity="0.01" />
            </linearGradient>
          </defs>

          {/* Horizontal grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((t) => {
            const y = padY + t * (H - padY * 2);
            const val = Math.round(maxVal * (1 - t));
            return (
              <g key={t}>
                <line x1={padX} y1={y} x2={W - padX} y2={y} stroke="#fce7f3" strokeWidth="1" strokeDasharray="4 4" />
                <text x={W - padX + 4} y={y + 4} fontSize="9" fill="#d1d5db" textAnchor="start">{val}</text>
              </g>
            );
          })}

          {/* Area fill */}
          <path d={areaPath} fill="url(#chartGrad)" />

          {/* Line */}
          <path d={linePath} fill="none" stroke="#fb7185" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

          {/* Hover zones + dots */}
          {points.map((pt, i) => (
            <g key={i} onMouseEnter={() => setHovered(i)}>
              {/* Invisible hover area */}
              <rect
                x={pt.x - (W / data.length) / 2}
                y={0}
                width={W / data.length}
                height={H}
                fill="transparent"
              />
              {/* Vertical hover line */}
              {hovered === i && (
                <line x1={pt.x} y1={padY} x2={pt.x} y2={H - padY} stroke="#fda4af" strokeWidth="1" strokeDasharray="3 3" />
              )}
              {/* Dot */}
              <circle
                cx={pt.x}
                cy={pt.y}
                r={hovered === i ? 5 : 3.5}
                fill={hovered === i ? '#fb7185' : '#fff'}
                stroke="#fb7185"
                strokeWidth="2"
                style={{ transition: 'r 0.15s' }}
              />
              {/* Tooltip */}
              {hovered === i && (
                <g>
                  <rect
                    x={pt.x - 22}
                    y={pt.y - 28}
                    width={44}
                    height={22}
                    rx="6"
                    fill="#fb7185"
                  />
                  <text x={pt.x} y={pt.y - 13} textAnchor="middle" fontSize="11" fill="white" fontWeight="bold">
                    {data[i].value}
                  </text>
                </g>
              )}
            </g>
          ))}
        </svg>

        {/* X-axis labels */}
        <div className="flex justify-between px-5 mt-1" dir="ltr">
          {data.map((d) => (
            <span key={d.label} className="text-xs text-gray-400 font-medium">{d.label}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
